import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { lawyerApi } from '../services/api';
import type { Lawyer } from '../types';

export function LawyerMarketplacePreview() {
  const { t } = useTranslation();
  const [lawyer, setLawyer] = useState<Lawyer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [presentationExpanded, setPresentationExpanded] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await lawyerApi.getLawyer();
        setLawyer(data as unknown as Lawyer);
      } catch {
        setError(t('common.error'));
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [t]);

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !lawyer) {
    return (
      <DashboardLayout>
        <div className="alert alert-danger">{error || t('common.error')}</div>
      </DashboardLayout>
    );
  }

  const isVerified = lawyer.verifiedLawyer === 'Verifierad';
  const presentation = lawyer.lawyerPresentation;
  const website = lawyer.website;

  return (
    <DashboardLayout>
      <div className="page-header">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="m-b-10">{t('nav.marketplacePreview', 'Marknadsplats förhandsgranskning')}</h5>
          </div>
          <Breadcrumb
            items={[
              { label: t('nav.home'), path: '/home' },
              { label: t('nav.settings'), path: '/settings/lawyer' },
              { label: t('nav.marketplacePreview', 'Marknadsplats förhandsgranskning') },
            ]}
          />
        </div>
      </div>

      {/* Info alert */}
      <div className="alert alert-info d-flex align-items-center gap-2 mb-4">
        <i className="feather-info" style={{ fontSize: 18 }}></i>
        <span>
          {t(
            'marketplacePreview.lawyerInfo',
            'Så här ser din profil ut för beställare under Extern hjälp. Uppdatera din information under Juristuppgifter.'
          )}
        </span>
      </div>

      {/* Platform message */}
      <div className="card border-0 bg-light mb-4">
        <div className="card-body">
          <div>
              <p className="mb-2">{t('marketplace.previewPlatformAnalysis')}</p>
              <p className="mb-2">{t('marketplace.previewPlatformApproach')}</p>
              <p className="mb-0 text-muted fst-italic">{t('marketplace.previewPlatformFeedback')}</p>
          </div>
        </div>
      </div>

      {/* Provider card - exact same design as renderBrowseReadOnlyCard in Marketplace.tsx */}
      <div className="card mb-3">
        <div className="card-body">
          {/* Header */}
          <div className="d-flex align-items-start gap-3 mb-3">
            <div
              className="avatar-text rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 48, height: 48, fontSize: 16, fontWeight: 600 }}
            >
              {getInitials(lawyer.name)}
            </div>
            <div className="flex-grow-1">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <span className="fw-bold">{lawyer.name}</span>
                {lawyer.title && <span className="text-muted">— {lawyer.title}</span>}
                {isVerified && (
                  <span className="badge bg-success">
                    <i className="feather-check me-1" style={{ fontSize: 10 }}></i>
                    {t('marketplace.provider.verified')}
                  </span>
                )}
                <span className={`badge ${lawyer.availability ? 'bg-success' : 'bg-secondary'}`}>
                  {lawyer.availability
                    ? t('marketplace.provider.availableForWork')
                    : t('marketplace.provider.notAvailable')}
                </span>
              </div>
            </div>
          </div>

          {/* Specialties */}
          {(lawyer.specialties || []).length > 0 && (
            <div className="mb-3">
              <div className="d-flex flex-wrap gap-1">
                {(lawyer.specialties || []).map((s) => (
                  <span key={s} className="badge bg-light text-dark border">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Practical info grid */}
          <div className="bg-light rounded p-3 mb-3">
            <div className="row g-2">
              {lawyer.hourlyRate && (
                <div className="col-6 col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="feather-tag text-muted" style={{ fontSize: 14 }}></i>
                    <div>
                      <div style={{ fontSize: 11 }} className="text-muted">
                        {t('marketplace.provider.hourlyRate')}
                      </div>
                      <div className="fw-semibold small">{lawyer.hourlyRate}</div>
                    </div>
                  </div>
                </div>
              )}
              {lawyer.responseTime && (
                <div className="col-6 col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="feather-clock text-muted" style={{ fontSize: 14 }}></i>
                    <div>
                      <div style={{ fontSize: 11 }} className="text-muted">
                        {t('marketplace.provider.responseTime')}
                      </div>
                      <div className="fw-semibold small">{lawyer.responseTime}</div>
                    </div>
                  </div>
                </div>
              )}
              {lawyer.experienceYears > 0 && (
                <div className="col-6 col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="feather-award text-muted" style={{ fontSize: 14 }}></i>
                    <div>
                      <div style={{ fontSize: 11 }} className="text-muted">
                        {t('marketplace.provider.experience')}
                      </div>
                      <div className="fw-semibold small">{lawyer.experienceYears} {t('common.years', 'år')}</div>
                    </div>
                  </div>
                </div>
              )}
              {lawyer.geographicArea && (
                <div className="col-6 col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="feather-map-pin text-muted" style={{ fontSize: 14 }}></i>
                    <div>
                      <div style={{ fontSize: 11 }} className="text-muted">
                        {t('marketplace.provider.geographicArea')}
                      </div>
                      <div className="fw-semibold small">{lawyer.geographicArea}</div>
                    </div>
                  </div>
                </div>
              )}
              {(lawyer.languages || []).length > 0 && (
                <div className="col-6 col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="feather-globe text-muted" style={{ fontSize: 14 }}></i>
                    <div>
                      <div style={{ fontSize: 11 }} className="text-muted">
                        {t('marketplace.provider.languages')}
                      </div>
                      <div className="fw-semibold small">{(lawyer.languages || []).join(', ')}</div>
                    </div>
                  </div>
                </div>
              )}
              {lawyer.fixedPriceAvailable && (
                <div className="col-6 col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="feather-tag text-muted" style={{ fontSize: 14 }}></i>
                    <div>
                      <div style={{ fontSize: 11 }} className="text-muted">
                        {t('marketplace.provider.fixedPrice')}
                      </div>
                      <div className="fw-semibold small text-success">{t('common.yes', 'Ja')}</div>
                    </div>
                  </div>
                </div>
              )}
              {lawyer.initialMeetingFree && (
                <div className="col-6 col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="feather-coffee text-muted" style={{ fontSize: 14 }}></i>
                    <div>
                      <div style={{ fontSize: 11 }} className="text-muted">
                        {t('marketplace.provider.initialMeeting')}
                      </div>
                      <div className="fw-semibold small text-success">
                        {t('marketplace.provider.initialMeetingFree')}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {lawyer.leadTime && (
                <div className="col-6 col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="feather-calendar text-muted" style={{ fontSize: 14 }}></i>
                    <div>
                      <div style={{ fontSize: 11 }} className="text-muted">
                        {t('marketplace.provider.leadTime')}
                      </div>
                      <div className="fw-semibold small">{lawyer.leadTime}</div>
                    </div>
                  </div>
                </div>
              )}
              {website && (
                <div className="col-6 col-md-4">
                  <div className="d-flex align-items-center gap-2">
                    <i className="feather-external-link text-muted" style={{ fontSize: 14 }}></i>
                    <div>
                      <div style={{ fontSize: 11 }} className="text-muted">
                        {t('marketplace.provider.website', 'Webbplats')}
                      </div>
                      <a
                        href={website.startsWith('http') ? website : `https://${website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fw-semibold small text-primary text-decoration-none"
                      >
                        {website.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Presentation toggle */}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setPresentationExpanded(!presentationExpanded)}
          >
            <i className={`feather-${presentationExpanded ? 'chevron-up' : 'chevron-down'} me-1`}></i>
            {presentationExpanded
              ? t('marketplace.provider.hidePresentation')
              : t('marketplace.provider.showPresentation')}
          </button>
          {presentationExpanded && presentation && (
            <div className="mt-3 p-3 border rounded bg-white">
              <small className="fw-semibold text-muted d-block mb-2">
                {t('marketplace.provider.aboutMe')}
              </small>
              <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: presentation }} />
            </div>
          )}
        </div>
      </div>

      {/* Edit link */}
      <div className="d-flex justify-content-end">
        <Link to="/settings/lawyer" className="btn btn-primary">
          <i className="feather-edit me-2"></i>
          {t('marketplacePreview.editDetails', 'Redigera uppgifter')}
        </Link>
      </div>
    </DashboardLayout>
  );
}
